/**
 * RegulatorySuggestionCard — Individual regulatory body suggestion card.
 *
 * Follows the SuggestionCard pattern from FormSchemaLlmPanel.tsx:
 * - Jurisdiction badge (color-coded)
 * - Confidence badge (green/yellow/red thresholds)
 * - Edge type badge
 * - Reason text
 * - Accept / Reject / Undo actions
 *
 * DRR-075: LLM-driven regulatory onboarding.
 */

import React, { useMemo } from 'react';
import {
  ActionIcon,
  Badge,
  Card,
  Collapse,
  Group,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconArrowBackUp,
  IconX,
} from '@tabler/icons-react';

import type { RegulatoryNodeSuggestion, SuggestionStatus } from '../hooks/useRegulatoryOnboarding';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const JURISDICTION_COLORS: Record<string, string> = {
  federal: 'red',
  state: 'orange',
  payer: 'green',
  standard: 'gray',
};

const JURISDICTION_LABELS: Record<string, string> = {
  federal: 'Federal',
  state: 'State',
  payer: 'Payer',
  standard: 'Standard',
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface RegulatorySuggestionCardProps {
  readonly suggestion: RegulatoryNodeSuggestion;
  readonly status: SuggestionStatus;
  readonly onAccept: (nodeId: string) => void;
  readonly onReject: (nodeId: string) => void;
  readonly onUndo: (nodeId: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RegulatorySuggestionCard({
  suggestion,
  status,
  onAccept,
  onReject,
  onUndo,
}: RegulatorySuggestionCardProps) {
  const [expanded, { toggle }] = useDisclosure(false);

  const confidenceColor = useMemo(() => {
    if (suggestion.confidence >= 0.8) return 'green';
    if (suggestion.confidence >= 0.6) return 'yellow';
    return 'red';
  }, [suggestion.confidence]);

  const isAccepted = status === 'accepted';
  const isRejected = status === 'rejected';
  const isPending = status === 'pending';

  return (
    <Card
      padding="sm"
      radius="md"
      withBorder
      style={{
        opacity: isRejected ? 0.5 : 1,
        borderLeftWidth: isAccepted ? 4 : 1,
        borderLeftColor: isAccepted
          ? 'var(--mantine-color-green-5)'
          : undefined,
        backgroundColor: isAccepted
          ? 'var(--mantine-color-green-0)'
          : undefined,
        transition: 'all 200ms ease',
      }}
    >
      <Stack gap="xs">
        {/* Header: jurisdiction + confidence + source */}
        <Group justify="space-between">
          <Group gap="xs">
            <Badge
              color={JURISDICTION_COLORS[suggestion.jurisdiction] || 'gray'}
              variant="filled"
              size="sm"
            >
              {JURISDICTION_LABELS[suggestion.jurisdiction] || suggestion.jurisdiction}
            </Badge>
            <Badge color={confidenceColor} variant="outline" size="sm">
              {Math.round(suggestion.confidence * 100)}%
            </Badge>
            {suggestion.source === 'llm' && (
              <Badge color="violet" variant="light" size="xs">
                AI
              </Badge>
            )}
          </Group>

          {/* Expand/collapse for notes */}
          {suggestion.notes && (
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                toggle();
              }}
            >
              {expanded ? (
                <IconChevronDown size={16} />
              ) : (
                <IconChevronRight size={16} />
              )}
            </ActionIcon>
          )}
        </Group>

        {/* Name + edge type */}
        <Group gap="xs">
          <Text
            size="sm"
            fw={600}
            td={isRejected ? 'line-through' : undefined}
          >
            {suggestion.name}
          </Text>
          <Badge
            color={suggestion.edgeType === 'REGULATED_BY' ? 'red' : 'gray'}
            variant={suggestion.edgeType === 'REGULATED_BY' ? 'filled' : 'outline'}
            size="xs"
          >
            {suggestion.edgeType === 'REGULATED_BY' ? 'Mandatory' : 'Voluntary'}
          </Badge>
          {suggestion.priority === 'primary' && (
            <Badge color="violet" variant="light" size="xs">
              Primary
            </Badge>
          )}
          {suggestion.conditional && (
            <Badge color="yellow" variant="light" size="xs">
              Conditional
            </Badge>
          )}
        </Group>

        {/* Reason */}
        <Text size="xs" c="dimmed">
          {suggestion.reason}
        </Text>

        {/* Expanded notes */}
        {suggestion.notes && (
          <Collapse in={expanded}>
            <Text size="xs" c="dimmed" fs="italic">
              {suggestion.notes}
            </Text>
          </Collapse>
        )}

        {/* Actions */}
        <Group justify="flex-end" gap="xs">
          {isPending && (
            <>
              <Tooltip label="Accept">
                <ActionIcon
                  variant="filled"
                  color="green"
                  size="sm"
                  onClick={() => onAccept(suggestion.nodeId)}
                >
                  <IconCheck size={14} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Reject">
                <ActionIcon
                  variant="outline"
                  color="red"
                  size="sm"
                  onClick={() => onReject(suggestion.nodeId)}
                >
                  <IconX size={14} />
                </ActionIcon>
              </Tooltip>
            </>
          )}
          {(isAccepted || isRejected) && (
            <Tooltip label="Undo">
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={() => onUndo(suggestion.nodeId)}
              >
                <IconArrowBackUp size={14} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Stack>
    </Card>
  );
}
